/**
 * Editor for the `settlement.custom_exceptions` platform setting.
 *
 * Shape (mirrors settlementExceptionRuleSchema in @transpay/shared-schema):
 *   { [methodCode]: { enabled, hour, dates: YYYY-MM-DD[], weekendOff: YYYY-MM-DD[] } }
 *
 * A checked method settles H+1 at its own `hour` instead of the regular
 * two-window schedule, and skips exception dates (see computeSettleAt). For a
 * checked method every Saturday/Sunday is an exception by default, so the
 * calendar shows weekends pre-selected. Clicking a weekend un-selects it
 * (→ `weekendOff`); clicking any other day selects it (→ `dates`). All dates are
 * GMT+7 calendar dates; today and later only.
 */
import { useState } from 'react';
import {
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Pill,
  Popover,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { IconCalendarEvent } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { PAYOK_PAYMENT_METHODS } from '../api/types';
import { useTranslation } from '../i18n/LocaleContext';

export type SettlementExceptionRule = {
  enabled: boolean;
  /** Settle hour 0-23 GMT+7. Absent on values saved before the field existed. */
  hour?: number;
  dates: string[];
  weekendOff: string[];
};
export type SettlementExceptionConfig = Partial<Record<string, SettlementExceptionRule>>;

const GMT7_OFFSET_MS = 7 * 60 * 60 * 1000;
/** MUST match DEFAULT_SETTLEMENT_EXCEPTION_HOUR_GMT7 in @transpay/shared-wallet. */
const DEFAULT_EXCEPTION_HOUR = 13;

/** Today's calendar date in GMT+7 — the settlement calendar's timezone. */
function todayGmt7(): string {
  return new Date(Date.now() + GMT7_OFFSET_MS).toISOString().slice(0, 10);
}

function isWeekend(ymd: string): boolean {
  const dow = new Date(`${ymd}T00:00:00Z`).getUTCDay();
  return dow === 0 || dow === 6;
}

/** MUST match isSettlementExceptionDate in @transpay/shared-wallet. */
function isExceptionDate(ymd: string, rule: SettlementExceptionRule): boolean {
  if (rule.dates.includes(ymd)) return true;
  return isWeekend(ymd) && !rule.weekendOff.includes(ymd);
}

/** `BCAVA` → `BCA VA`; non-VA codes are shown as-is. */
export function paymentMethodLabel(code: string): string {
  return /VA$/.test(code) ? `${code.slice(0, -2)} VA` : code;
}

function toggle(list: string[], ymd: string): string[] {
  return list.includes(ymd) ? list.filter((d) => d !== ymd) : [...list, ymd].sort();
}

function asConfig(value: unknown): SettlementExceptionConfig {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as SettlementExceptionConfig)
    : {};
}

export function SettlementExceptionEditor({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (v: SettlementExceptionConfig) => void;
}) {
  const { t } = useTranslation();
  const config = asConfig(value);
  const today = todayGmt7();

  // Rebuild in catalog order on every change so key order is stable (the
  // Settings row's dirty check compares JSON strings), and drop dates that have
  // passed — they can no longer affect any settlement.
  const emit = (method: string, rule: SettlementExceptionRule | null) => {
    const next: SettlementExceptionConfig = {};
    for (const code of PAYOK_PAYMENT_METHODS) {
      const r = code === method ? rule : config[code];
      if (!r) continue;
      next[code] = {
        enabled: r.enabled,
        hour: r.hour ?? DEFAULT_EXCEPTION_HOUR,
        dates: r.dates.filter((d) => d >= today),
        weekendOff: r.weekendOff.filter((d) => d >= today),
      };
    }
    onChange(next);
  };

  const setChecked = (method: string, checked: boolean) => {
    const existing = config[method];
    if (checked) {
      emit(method, {
        enabled: true,
        hour: existing?.hour,
        dates: existing?.dates ?? [],
        weekendOff: existing?.weekendOff ?? [],
      });
      return;
    }
    // Keep the customised config of an unchecked method so re-checking restores
    // it; drop the entry entirely when there is nothing worth keeping.
    const customised =
      !!existing &&
      (existing.dates.length > 0 ||
        existing.weekendOff.length > 0 ||
        (existing.hour ?? DEFAULT_EXCEPTION_HOUR) !== DEFAULT_EXCEPTION_HOUR);
    emit(method, customised && existing ? { ...existing, enabled: false } : null);
  };

  const enabledMethods = PAYOK_PAYMENT_METHODS.filter((code) => config[code]?.enabled);

  return (
    <Stack gap="md">
      <div>
        <Text size="sm" fw={500}>
          {t('settings.exc.methodsLabel')}
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          {t('settings.exc.methodsDesc')}
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="xs" verticalSpacing="xs">
          {PAYOK_PAYMENT_METHODS.map((code) => (
            <Checkbox
              key={code}
              size="sm"
              label={paymentMethodLabel(code)}
              checked={config[code]?.enabled === true}
              onChange={(e) => setChecked(code, e.currentTarget.checked)}
            />
          ))}
        </SimpleGrid>
      </div>

      <div>
        <Text size="sm" fw={500}>
          {t('settings.exc.datesLabel')}
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          {t('settings.exc.datesDesc')}
        </Text>
        {enabledMethods.length === 0 ? (
          <Text size="xs" c="dimmed" fs="italic">
            {t('settings.exc.noneChecked')}
          </Text>
        ) : (
          <Stack gap="xs">
            {enabledMethods.map((code) => (
              <MethodExceptionRow
                key={code}
                code={code}
                rule={config[code] as SettlementExceptionRule}
                today={today}
                onChange={(rule) => emit(code, rule)}
              />
            ))}
          </Stack>
        )}
      </div>
    </Stack>
  );
}

function MethodExceptionRow({
  code,
  rule,
  today,
  onChange,
}: {
  code: string;
  rule: SettlementExceptionRule;
  today: string;
  onChange: (rule: SettlementExceptionRule) => void;
}) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const onDayClick = (ymd: string) => {
    if (ymd < today) return;
    if (isWeekend(ymd) && !rule.dates.includes(ymd)) {
      onChange({ ...rule, weekendOff: toggle(rule.weekendOff, ymd) });
    } else {
      onChange({ ...rule, dates: toggle(rule.dates, ymd) });
    }
  };

  const hour = rule.hour ?? DEFAULT_EXCEPTION_HOUR;
  const extra = rule.dates.filter((d) => d >= today);
  const weekendOff = rule.weekendOff.filter((d) => d >= today);

  return (
    <Paper withBorder p="sm" radius="sm">
      <Group justify="space-between" align="center" wrap="wrap" gap="xs">
        <div>
          <Text size="sm" fw={600}>
            {paymentMethodLabel(code)}
          </Text>
          <Text size="xs" c="dimmed">
            {[
              t('settings.exc.summaryHour', { hour: String(hour).padStart(2, '0') }),
              t('settings.exc.weekendDefault'),
              extra.length > 0 ? t('settings.exc.extraCount', { n: extra.length }) : null,
              weekendOff.length > 0
                ? t('settings.exc.weekendOffCount', { n: weekendOff.length })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </div>
        <Group gap="xs" align="flex-end" wrap="wrap">
          <NumberInput
            size="xs"
            w={190}
            label={t('settings.exc.hourLabel')}
            min={0}
            max={23}
            allowDecimal={false}
            value={hour}
            onChange={(v) =>
              onChange({ ...rule, hour: typeof v === 'number' ? v : DEFAULT_EXCEPTION_HOUR })
            }
          />
          <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            withArrow
            shadow="md"
            withinPortal
          >
            <Popover.Target>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconCalendarEvent size={14} />}
                onClick={() => setOpened((o) => !o)}
              >
                {t('settings.exc.pickDates')}
              </Button>
            </Popover.Target>
            <Popover.Dropdown>
              <Calendar
                minDate={dayjs(today).toDate()}
                getDayProps={(date) => {
                  const ymd = dayjs(date).format('YYYY-MM-DD');
                  return {
                    selected: ymd >= today && isExceptionDate(ymd, rule),
                    onClick: () => onDayClick(ymd),
                  };
                }}
              />
              <Text size="xs" c="dimmed" ta="center" mt={6}>
                {t('settings.exc.calendarHint')}
              </Text>
            </Popover.Dropdown>
          </Popover>
        </Group>
      </Group>

      {extra.length > 0 && (
        <DatePills
          label={t('settings.exc.extraDates')}
          dates={extra}
          onRemove={(ymd) => onChange({ ...rule, dates: rule.dates.filter((d) => d !== ymd) })}
        />
      )}
      {weekendOff.length > 0 && (
        <DatePills
          label={t('settings.exc.weekendOffDates')}
          dates={weekendOff}
          onRemove={(ymd) =>
            onChange({ ...rule, weekendOff: rule.weekendOff.filter((d) => d !== ymd) })
          }
        />
      )}
    </Paper>
  );
}

function DatePills({
  label,
  dates,
  onRemove,
}: {
  label: string;
  dates: string[];
  onRemove: (ymd: string) => void;
}) {
  return (
    <Group gap={6} mt="xs" align="center">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {dates.map((ymd) => (
        <Pill key={ymd} size="sm" withRemoveButton onRemove={() => onRemove(ymd)}>
          {dayjs(ymd).format('ddd, D MMM YYYY')}
        </Pill>
      ))}
    </Group>
  );
}
